import { LayerKey } from '@festina-life/api'
import type { Optional, ValueOf } from '@festina-life/core'

import type { EnumDefinition } from '../../uiModels/enumLiterals/types'
import type { IndexedGlMapOperations } from '../../uiModels/glMapOperations/types'
import type {
  IndexedParameterDefinitionCreateOperations,
  IndexedParameterValueOperations,
} from '../../uiModels/parameterOperations/types'
import type { IndexedReconciliationSpecOperations } from '../../uiModels/reconciliationSpecOperations/types'
import type { IndexedRegisteredTenantCreateOperations } from '../../uiModels/registeredTenantCreateOperations/types'
import type { IndexedTemplateOperations } from '../../uiModels/templateOperations/types'
import type { OperationsContext } from '../../workspace/operationsContext'
import { isSameTabTarget } from './isSameTabTarget'
import type { TabLink } from './types'

type FilterOperations = Pick<
  OperationsContext,
  | 'templateOperations'
  | 'parameterDefinitionCreateOperations'
  | 'parameterValueEntityOperations'
  | 'configuredEnums'
  | 'registeredTenantCreateOperations'
  | 'glMapOperations'
  | 'reconciliationSpecCreateOperations'
  | 'allAnnulOperationsByKey'
>

type IndexedOperation =
  | IndexedTemplateOperations
  | IndexedParameterDefinitionCreateOperations
  | IndexedParameterValueOperations
  | EnumDefinition
  | IndexedRegisteredTenantCreateOperations
  | IndexedGlMapOperations
  | IndexedReconciliationSpecOperations

const isIndexedTemplateOperations = (
  indexedOperation: IndexedOperation,
): indexedOperation is IndexedTemplateOperations =>
  !Array.isArray(indexedOperation) && 'templateId' in indexedOperation

const isIndexedParameterDefinitionCreateOperations = (
  indexedOperation: IndexedOperation,
): indexedOperation is IndexedParameterDefinitionCreateOperations =>
  !Array.isArray(indexedOperation) && 'parameterId' in indexedOperation

const isIndexedParameterValueOperations = (
  indexedOperation: IndexedOperation,
): indexedOperation is IndexedParameterValueOperations =>
  Array.isArray(indexedOperation)

const isEnumDefinition = (
  indexedOperation: IndexedOperation,
): indexedOperation is EnumDefinition =>
  !Array.isArray(indexedOperation) &&
  'name' in indexedOperation &&
  'literals' in indexedOperation

const isIndexedRegisteredTenantCreateOperations = (
  indexedOperation: IndexedOperation,
): indexedOperation is IndexedRegisteredTenantCreateOperations =>
  !Array.isArray(indexedOperation) && 'tenantId' in indexedOperation

const isIndexedGlMapOperations = (
  indexedOperation: IndexedOperation,
): indexedOperation is IndexedGlMapOperations =>
  !Array.isArray(indexedOperation) && 'mappingId' in indexedOperation

const isIndexedReconciliationSpecOperations = (
  indexedOperation: IndexedOperation,
): indexedOperation is IndexedReconciliationSpecOperations =>
  !Array.isArray(indexedOperation) && 'specId' in indexedOperation

const areAllOperationsAnnulled = (valueForId: IndexedOperation): boolean => {
  if (isIndexedTemplateOperations(valueForId)) {
    return valueForId.operations.every(
      ({ annulOperations }) => !!annulOperations.length,
    )
  }

  if (isIndexedParameterDefinitionCreateOperations(valueForId)) {
    return valueForId.operations.every(
      ({ annulOperations }) => !!annulOperations.length,
    )
  }

  if (isIndexedParameterValueOperations(valueForId)) {
    return valueForId.every(({ annulOperations }) => !!annulOperations.length)
  }

  if (isEnumDefinition(valueForId)) {
    return (
      (!valueForId.internalOperation || !!valueForId.annulOperations.length) &&
      valueForId.literals.every(
        (literal) =>
          literal.type === 'modeled' || !!literal.annulOperations.length,
      )
    )
  }

  if (isIndexedRegisteredTenantCreateOperations(valueForId)) {
    return valueForId.operations.every(
      ({ annulOperations }) => !!annulOperations.length,
    )
  }

  if (isIndexedGlMapOperations(valueForId)) {
    return valueForId.operations.every(
      ({ annulOperations }) => !!annulOperations.length,
    )
  }

  if (isIndexedReconciliationSpecOperations(valueForId)) {
    return valueForId.operations.every(
      ({ annulOperations }) => !!annulOperations.length,
    )
  }

  return false
}

/**
 * Whether the item a tab link points at still exists in the loaded operations data.
 */
export const isExistentTabLink = (
  tabLink: TabLink,
  operations: FilterOperations,
) => {
  const {
    templateOperations,
    parameterDefinitionCreateOperations,
    parameterValueEntityOperations,
    configuredEnums,
    registeredTenantCreateOperations,
    glMapOperations,
    reconciliationSpecCreateOperations,
    allAnnulOperationsByKey,
  } = operations
  const { id, block, layer, variant } = tabLink
  const layerKey = new LayerKey(layer)
  const hasId = (
    operations: ValueOf<Omit<FilterOperations, 'allAnnulOperationsByKey'>>,
  ) => {
    const layerValue = operations.get(block)?.get(layerKey)

    if (!layerValue) {
      return false
    }

    const valueForId = layerValue.get(id)

    return !!valueForId && !areAllOperationsAnnulled(valueForId)
  }

  switch (variant) {
    case 'template':
    case 'library':
      return hasId(templateOperations)

    case 'parameterDefinition':
      return (
        hasId(parameterDefinitionCreateOperations) ||
        hasId(parameterValueEntityOperations)
      )
    case 'tenant':
      return hasId(registeredTenantCreateOperations)

    case 'enumDefinition':
      return hasId(configuredEnums)

    case 'glMap':
      return hasId(glMapOperations)

    case 'reconciliationSpec':
      return hasId(reconciliationSpecCreateOperations)

    case 'annulOperation': {
      const annulOp = allAnnulOperationsByKey.get(id)
      if (!annulOp) {
        return false
      }
      // For draft tabs, the backing draft operation must still exist
      if (tabLink.isDraft) {
        return annulOp.isDraft
      }
      // For committed tabs, the operation just needs to exist in the index
      return true
    }

    case 'scratchpad':
      return true
  }
}

/**
 * Filters tab links to only include those whose items still exist in the
 * loaded operations data. Raw localStorage is never modified.
 *
 * While a configuration item is being viewed, the tab for that route is the only
 * active one: when the viewed item does not exist none of the surviving tabs is
 * the one being looked at, so none may be shown as active.
 *
 * Otherwise, if the active tab was removed the first surviving tab is promoted to
 * active so downstream hooks always see a valid active-tab state.
 */
export const filterExistentTabLinks = ({
  tabLinks,
  operations,
  currentPathAsTabLink,
}: {
  tabLinks: TabLink[]
  operations: FilterOperations
  currentPathAsTabLink?: Optional<TabLink>
}): TabLink[] => {
  const filtered = tabLinks.filter((tab) => isExistentTabLink(tab, operations))

  if (currentPathAsTabLink) {
    return filtered.map((tab) => {
      const isActive = isSameTabTarget(tab, currentPathAsTabLink)
      const isActiveStateCorrect = tab.isActive === isActive

      return isActiveStateCorrect ? tab : { ...tab, isActive }
    })
  }

  if (filtered.length === 0 || filtered.some((tab) => tab.isActive)) {
    return filtered
  }

  return filtered.map((tab, i) => (i === 0 ? { ...tab, isActive: true } : tab))
}
